import React from 'react'
import Popover from 'react-bootstrap/Popover'
import { XCircle, CheckCircle } from 'react-bootstrap-icons'

// password helper types and functions - start

export interface PasswordValidationType {
  validEightOrMore: boolean
  validLettersAndNumbers: boolean
  validDoesNotConsecRepeat: boolean
  validUpperCaseLetter: boolean
  validSpecialCharacter: boolean
  passwordIsValid: boolean
  password: string
}

export interface CreatePasswordValidationType {
  createPasswordStyle: string
  showCheckEightOrMore: boolean
  showCheckUsesBothLettersAndNumbers: boolean
  showCheckDoesNotConsecRepeat: boolean
  showCheckUpperCaseLetter: boolean
  showCheckSpecialCharacter: boolean
  errorMessages: string[]
  isValid: boolean
  password: string
}

export interface CurrentPasswordValidationType {
  isValid: boolean
  errorMessage: string | null
}

export interface ConfirmPasswordValidationType {
  confirmPasswordStyle: string
  isValid: boolean
  errorMessage: string | null
  password: string
}

export const hasConsecutiveNumbers = (str: string): boolean => {
  const pattern = /\\b([a-zA-Z0-9])\\1\\1+\\b/
  return pattern.test(str)
}

export function atLeastOneUpperCaseLetter(password: string): boolean {
  return /(?=.*?[A-Z]).+/.test(password)
}

export function atLeastOneSpecialCharacter(password: string): boolean {
  return /[#-/:-@{-~!"^_`[\]]/.test(password)
}

export function atLeastOneLetterAndNumber(password: string): boolean {
  return /(?=.*?[0-9])(?=.*?[A-Za-z]).+/.test(password)
}

export function hasTriple(password: string): boolean {
  return /(.)\1\1/.test(password)
}

export function validatePassword(password: string): PasswordValidationType {
  const result = {
    validEightOrMore: false,
    validLettersAndNumbers: false,
    validDoesNotConsecRepeat: true,
    validUpperCaseLetter: false,
    validSpecialCharacter: false,
    passwordIsValid: false,
    password: ''
  }

  if (password && password.length > 0) {
    result.password = password
    if (password.length > 7) {
      result.validEightOrMore = true
    }

    if (atLeastOneLetterAndNumber(password)) {
      result.validLettersAndNumbers = true
    }

    if (hasTriple(password)) {
      result.validDoesNotConsecRepeat = false
    }

    if (hasConsecutiveNumbers(password)) {
      result.validDoesNotConsecRepeat = false
    }

    if (atLeastOneUpperCaseLetter(password)) {
      result.validUpperCaseLetter = true
    }

    if (atLeastOneSpecialCharacter(password)) {
      result.validSpecialCharacter = true
    }
  }

  if (
    result.validEightOrMore &&
    result.validLettersAndNumbers &&
    result.validDoesNotConsecRepeat &&
    result.validUpperCaseLetter &&
    result.validSpecialCharacter
  ) {
    result.passwordIsValid = true
  }

  return result
}

export function createPasswordValidationTypeInitial(): CreatePasswordValidationType {
  const result = {
    createPasswordStyle: 'form-control',
    showCheckEightOrMore: false,
    showCheckUsesBothLettersAndNumbers: false,
    showCheckDoesNotConsecRepeat: true,
    showCheckUpperCaseLetter: false,
    showCheckSpecialCharacter: false,
    errorMessages: [],
    isValid: false,
    password: ''
  }

  return result
}

export function currentPasswordValidationTypeInitial(): CurrentPasswordValidationType {
  const result = {
    isValid: true,
    errorMessage: ''
  }
  return result
}

export function confirmPasswordValidationTypeInitial(): ConfirmPasswordValidationType {
  const result = {
    confirmPasswordStyle: 'form-control',
    isValid: false,
    errorMessage: '',
    password: ''
  }

  return result
}

export function confirmPasswordValidation(
  confirmPassword: string,
  createPassword: string
): ConfirmPasswordValidationType {
  const result = confirmPasswordValidationTypeInitial()

  result.password = confirmPassword
  if (confirmPassword === createPassword) {
    result.isValid = true
    result.confirmPasswordStyle = 'form-control is-valid'
  } else if (confirmPassword.length <= 0) {
    result.confirmPasswordStyle = 'form-control is-invalid form-input-error'
    result.errorMessage = 'This field is required'
  } else {
    result.confirmPasswordStyle = 'form-control is-invalid form-input-error'
    result.errorMessage = 'Your passwords do not match'
  }

  return result
}

export function createPasswordValidation(
  password: string
): CreatePasswordValidationType {
  const result = createPasswordValidationTypeInitial()
  const passwordValidationResult = validatePassword(password)

  result.password = password
  result.isValid = passwordValidationResult.passwordIsValid
  if (password.length == 0) {
    result.createPasswordStyle = 'form-control'
  } else if (passwordValidationResult.passwordIsValid) {
    result.createPasswordStyle = 'form-control is-valid'
  } else {
    result.createPasswordStyle = 'form-control is-invalid'
  }
  result.showCheckEightOrMore = passwordValidationResult.validEightOrMore
  result.showCheckUsesBothLettersAndNumbers =
    passwordValidationResult.validLettersAndNumbers
  result.showCheckDoesNotConsecRepeat =
    passwordValidationResult.validDoesNotConsecRepeat
  result.showCheckUpperCaseLetter =
    passwordValidationResult.validUpperCaseLetter
  result.showCheckSpecialCharacter =
    passwordValidationResult.validSpecialCharacter

  if (!result.showCheckEightOrMore) {
    result.errorMessages.push(
      'Your password must contain a minimum of 8 characters.'
    )
  }

  if (!result.showCheckUsesBothLettersAndNumbers) {
    result.errorMessages.push(
      'Your password must contain both letters and numbers.'
    )
  }

  if (!result.showCheckDoesNotConsecRepeat) {
    result.errorMessages.push(
      'Your password cannot contain consecutive repeating numbers or letters.'
    )
  }

  if (!result.showCheckUpperCaseLetter) {
    result.errorMessages.push(
      'Your password must contain at least one upper case letter.'
    )
  }

  if (!result.showCheckSpecialCharacter) {
    result.errorMessages.push(
      'Your password must contain at least one special character.'
    )
  }

  return result
}

export function getPasswordRulesPopover(
  createPasswordValidationResult: CreatePasswordValidationType
): JSX.Element {
  const { errorMessages } = createPasswordValidationResult
  const popover = (
    <Popover id='popover-basic' data-testid='password-popover'>
      <Popover.Title as='h3'>Create a password that:</Popover.Title>
      <Popover.Content>
        <ul className='ui-password-popover'>
          <li
            style={{ listStyleType: 'none' }}
            className={
              createPasswordValidationResult.showCheckEightOrMore
                ? 'text-success'
                : errorMessages.length
                ? 'text-danger'
                : ''
            }
          >
            {!createPasswordValidationResult.showCheckEightOrMore ? (
              errorMessages.length ? (
                <XCircle color='red' className='mr-2' />
              ) : (
                <span className='ui-bull'>&bull;</span>
              )
            ) : (
              <CheckCircle color='green' className='mr-2' />
            )}
            Contains 8 or more characters
          </li>
          <li
            style={{ listStyleType: 'none' }}
            className={
              createPasswordValidationResult.showCheckUsesBothLettersAndNumbers
                ? 'text-success'
                : errorMessages.length
                ? 'text-danger'
                : ''
            }
          >
            {!createPasswordValidationResult.showCheckUsesBothLettersAndNumbers ? (
              errorMessages.length ? (
                <XCircle color='red' className='mr-2' />
              ) : (
                <span className='ui-bull'>&bull;</span>
              )
            ) : (
              <CheckCircle color='green' className='mr-2' />
            )}
            Uses both letters and numbers
          </li>

          <li
            style={{ listStyleType: 'none' }}
            className={
              createPasswordValidationResult.showCheckUpperCaseLetter
                ? 'text-success'
                : errorMessages.length
                ? 'text-danger'
                : ''
            }
          >
            {!createPasswordValidationResult.showCheckUpperCaseLetter ? (
              errorMessages.length ? (
                <XCircle color='red' className='mr-2' />
              ) : (
                <span className='ui-bull'>&bull;</span>
              )
            ) : (
              <CheckCircle color='green' className='mr-2' />
            )}
            At least one upper case letter
          </li>

          <li
            style={{ listStyleType: 'none' }}
            className={
              createPasswordValidationResult.showCheckSpecialCharacter
                ? 'text-success'
                : errorMessages.length
                ? 'text-danger'
                : ''
            }
          >
            {!createPasswordValidationResult.showCheckSpecialCharacter ? (
              errorMessages.length ? (
                <XCircle color='red' className='mr-2' />
              ) : (
                <span className='ui-bull'>&bull;</span>
              )
            ) : (
              <CheckCircle color='green' className='mr-2' />
            )}
            At least one special character
          </li>

          <li
            style={{ listStyleType: 'none' }}
            className={
              createPasswordValidationResult.showCheckDoesNotConsecRepeat
                ? errorMessages.length || createPasswordValidationResult.isValid
                  ? 'text-success'
                  : ''
                : 'text-danger'
            }
          >
            {!createPasswordValidationResult.showCheckDoesNotConsecRepeat ? (
              <XCircle color='red' className='mr-2' />
            ) : errorMessages.length ||
              createPasswordValidationResult.isValid ? (
              <CheckCircle color='green' className='mr-2' />
            ) : (
              <span className='ui-bull'>&bull;</span>
            )}
            Does not consecutively repeat characters
          </li>
        </ul>
      </Popover.Content>
    </Popover>
  )
  return popover
}

// password helper types and functions - finish
